import { ArrowUpRightIcon } from '@phosphor-icons/react'
import { Link, type LinkProps } from 'react-router'
import * as styles from './threat-model-stat-preview.css.ts'

/**
 * One line of a masthead stat's preview: a name and an optional descriptor drawn from the item behind
 * the count. The caller derives these from the already-fetched threat-model payload — the preview never
 * fetches — so which field becomes the descriptor is the caller's decision, not this component's.
 */
export type StatPreviewItem = {
  key: string
  primary: string
  secondary?: string
}

export type ThreatModelStatPreviewProps = {
  label: string
  /** The view the stat links into, named in the footer link ("Open in Attack surface"). */
  destination: string
  /** The full count, used for the header and to compute the "+N more" line. */
  total: number
  items: StatPreviewItem[]
  to: LinkProps['to']
  previewLimit?: number
}

// Five lines is enough to convey what a count holds without turning the hovercard into the inventory it
// previews; the inventories run to 4–11 items each.
const DEFAULT_PREVIEW_LIMIT = 5

/**
 * The hovercard body for a masthead stat: the top N items behind the count, one line each, and a link
 * into the view that holds them all. Rendered inside a Radix `HoverCard.Content`, mirroring how
 * `FindingHoverCard` previews a linked finding.
 */
export const ThreatModelStatPreview: React.FC<ThreatModelStatPreviewProps> = ({
  label,
  destination,
  total,
  items,
  to,
  previewLimit = DEFAULT_PREVIEW_LIMIT,
}) => {
  const shown = items.slice(0, previewLimit)
  const remaining = total - shown.length

  return (
    // The test id scopes e2e assertions to the portalled preview, whose text otherwise collides with
    // the trigger tile's. Radix's `HoverCard.Content` carries no implicit ARIA role to key off instead.
    <div className={styles.card} data-testid="threat-model-stat-preview">
      <div className={styles.header}>
        <span className={styles.title}>{label}</span>
        <span className={styles.count}>{total}</span>
      </div>
      <ul className={styles.list}>
        {shown.map(item => (
          <li key={item.key} className={styles.item}>
            <span className={styles.itemPrimary}>{item.primary}</span>
            {item.secondary && <span className={styles.itemSecondary}>{item.secondary}</span>}
          </li>
        ))}
      </ul>
      {remaining > 0 && <p className={styles.more}>+{remaining} more</p>}
      <Link to={to} className={styles.link}>
        Open in {destination}
        <ArrowUpRightIcon size={12} weight="bold" />
      </Link>
    </div>
  )
}
